import {Component, Signal, inject, signal} from '@angular/core';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatIconModule} from '@angular/material/icon';
import {MatButtonModule} from '@angular/material/button';
import {FormsModule} from '@angular/forms';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {MatInputModule} from '@angular/material/input';
import {ChatService} from '../../services/chat.service';
import {SideBarService} from '../../services/sidebar.service';
import {PinnedQueryService} from '../../services/pinned-query.service';
import { NgFor, NgIf } from '@angular/common';
import {MatSlideToggleModule} from '@angular/material/slide-toggle';
import {MatDividerModule} from '@angular/material/divider';
import {MatTooltipModule} from '@angular/material/tooltip';
import {MatSnackBar} from '@angular/material/snack-bar';
import { GroupedSessions, SessionResponseShort } from '../../interfaces/sessions';
import { formatSessionName } from '../../utils/sessions.helper';
import { PinnedQuery } from '../../interfaces/pinned-query';


@Component({
    selector: 'fyxt-side-bar',
    templateUrl: './sidebar.component.html',
    styleUrls: ['./sidebar.component.scss'],
    standalone: true,
    imports: [
      MatFormFieldModule,
      MatInputModule,
      MatIconModule,
      MatButtonModule,
      FormsModule,
      MatProgressSpinnerModule,
      NgFor,
      NgIf,
      MatSlideToggleModule,
      MatDividerModule,
      MatTooltipModule,
    ]
})
export class SideBarComponent {
  private readonly snackBar = inject(MatSnackBar);
  readonly pinnedQueryService = inject(PinnedQueryService);
  readonly pinnedExpanded = signal(true);

  constructor(
    public chatService: ChatService,
    private sideBarService: SideBarService,
  ) {
  }

  get groupedSessions(): Signal<GroupedSessions[]> {
    return this.sideBarService.shortGroupedSessions
  }

  get isShortGroupedChatsLoading(): Signal<boolean> {
    return this.sideBarService.isShortGroupedChatsLoading
  }

  get currentChat(): string {
    return this.sideBarService.currentChat
  }

  get pinnedQueries(): Signal<PinnedQuery[]> {
    return this.pinnedQueryService.pinnedQueries;
  }

  togglePinnedSection(event?: Event): void {
    event?.stopPropagation();
    this.pinnedExpanded.update((open) => !open);
  }

  openChat(shortSession: SessionResponseShort) {
    this.sideBarService.currentChat = shortSession.id;
    this.chatService.loadChatMessages(shortSession.id)
  }

  turnStream() {
    this.chatService.isStream = !this.chatService.isStream;
  }

  getSessionDisplayName(session: SessionResponseShort): string {
    return formatSessionName(session.name, session.created_at);
  }

  truncatePinnedText(text: string, max = 48): string {
    const value = (text || '').trim();
    if (value.length <= max) {
      return value;
    }
    return `${value.slice(0, max - 1)}…`;
  }

  usePinnedQuery(pinned: PinnedQuery): void {
    if (this.chatService.isLoading) {
      return;
    }
    this.chatService.isOpened.set(false);
    this.pinnedQueryService.requestSend(pinned.text);
  }

  unpinQuery(event: Event, pinned: PinnedQuery): void {
    event.stopPropagation();
    this.pinnedQueryService.unpinQuery(pinned.id).subscribe({
      error: () => {
        this.snackBar.open('Failed to unpin question. Please try again.', 'Close', {
          duration: 5000,
          panelClass: ['error-snackbar'],
        });
      },
    });
  }
}
